import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { acquireSocket, releaseSocket } from "../../utils/socket";
import dayjs from "dayjs";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CloseIcon from "@mui/icons-material/Close";
import apiClient from "../../utils/clients/axios";
import { handleToastMsg } from "../Toast";
import alertSound from "../../assets/new-ticket-alert.mp3";
import emergencySound from "../../assets/ambulance-siren.mp3";
import styles from "./NewTicketAlert.module.css";

const ALERTS_URL = "/api/quality_control/report_issue/alerts";
const NEW_ALERT_EVENT = "reportIssue:newAlert";
const ACK_EVENT = "reportIssue:alertAck";
// "Solution Rejected" popups: one per decline of the manager's solution
const REJECTED_ALERTS_URL = `${ALERTS_URL}/rejected`;
const REJECTED_ALERT_EVENT = "reportIssue:rejectedAlert";
const REJECTED_ACK_EVENT = "reportIssue:rejectedAlertAck";
// Manager's task sheet page; ?action=resolve opens the ticket's resolve form
const RESOLVE_PATH = "/task-sheets-resolve";
// Task sheet lists (react-query key), refreshed when an alert comes in
const TASK_SHEETS_QUERY_KEY = "task-sheets-report-issue";

// Rejected alerts are keyed by their decline, apart from new ticket alerts
// (keyed by issue id), so both can be open for the same issue
const rejectedKey = (approvalId) => `rejected-${approvalId}`;
const toRejectedAlert = (alert) => ({
  ...alert,
  id: rejectedKey(alert.approvalId),
  kind: "rejected",
});

// The beep keeps going after a quick close until this long after the popup opened
const MIN_BEEP_MS = 5 * 1000;
// Cards drawn behind the top one; the rest stay hidden until they surface
const MAX_STACKED = 5;
const STACK_OFFSET_PX = 14;

const mergeAlerts = (current, incoming) => {
  const known = new Set(current.map((alert) => alert.id));
  const added = incoming.filter((alert) => !known.has(alert.id));
  return added.length ? [...current, ...added] : current;
};

// Popup + beep for every new task sheet issue assigned to the logged-in
// reporting manager, and for every decline of their solution ("Solution
// Rejected", with the approver's comment). Open alerts come back on every
// login until the manager closes them. The beep loops while any popup is open, and for at least
// MIN_BEEP_MS after the latest one opened. While an Emergency ticket is open
// an ambulance siren plays instead of the beep.
function NewTicketAlert() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [alerts, setAlerts] = useState([]);

  const audiosRef = useRef(null); // { normal, siren }
  const openIdsRef = useRef(new Set());
  const emergencyIdsRef = useRef(new Set());
  // Sound kept for the MIN_BEEP_MS tail once every popup is closed
  const lastKindRef = useRef("normal");
  const beepUntilRef = useRef(0);
  const stopTimerRef = useRef(null);
  const gestureHandlerRef = useRef(null);

  const removeGestureHandler = () => {
    const handler = gestureHandlerRef.current;
    if (!handler) return;
    window.removeEventListener("pointerdown", handler);
    window.removeEventListener("keydown", handler);
    gestureHandlerRef.current = null;
  };

  const shouldBeep = () =>
    openIdsRef.current.size > 0 || Date.now() < beepUntilRef.current;

  const stopAudio = (audio) => {
    if (!audio || audio.paused) return;
    audio.pause();
    audio.currentTime = 0;
  };

  // Starts, keeps, switches or stops the looping sound to match the open
  // popups: siren while any emergency ticket is open, otherwise the beep
  const syncSound = useCallback(() => {
    const audios = audiosRef.current;
    if (!audios) return;
    clearTimeout(stopTimerRef.current);

    if (!shouldBeep()) {
      removeGestureHandler();
      stopAudio(audios.normal);
      stopAudio(audios.siren);
      return;
    }

    if (openIdsRef.current.size) {
      lastKindRef.current = emergencyIdsRef.current.size ? "siren" : "normal";
    } else {
      stopTimerRef.current = setTimeout(
        syncSound,
        beepUntilRef.current - Date.now()
      );
    }

    const kind = lastKindRef.current;
    const audio = audios[kind];
    stopAudio(kind === "siren" ? audios.normal : audios.siren);

    if (!audio.paused) return;
    audio.play().catch(() => {
      // Autoplay is blocked until the user interacts with the page (e.g.
      // after a refresh); start on the first click or key press instead
      if (gestureHandlerRef.current) return;
      const onGesture = () => {
        removeGestureHandler();
        syncSound();
      };
      gestureHandlerRef.current = onGesture;
      window.addEventListener("pointerdown", onGesture);
      window.addEventListener("keydown", onGesture);
    });
  }, []);

  const addAlerts = useCallback(
    (incoming) => {
      const fresh = (incoming || []).filter(
        (alert) => !openIdsRef.current.has(alert.id)
      );
      if (!fresh.length) return;

      fresh.forEach((alert) => {
        openIdsRef.current.add(alert.id);
        // Siren for new emergency tickets only, not their rejections
        if (alert.isEmergency && alert.kind !== "rejected") {
          emergencyIdsRef.current.add(alert.id);
        }
      });
      beepUntilRef.current = Date.now() + MIN_BEEP_MS;
      setAlerts((current) => mergeAlerts(current, fresh));
      syncSound();
    },
    [syncSound]
  );

  const removeAlerts = useCallback(
    (ids) => {
      ids.forEach((id) => {
        openIdsRef.current.delete(id);
        emergencyIdsRef.current.delete(id);
      });
      setAlerts((current) => current.filter((alert) => !ids.includes(alert.id)));
      syncSound();
    },
    [syncSound]
  );

  const fetchAlerts = useCallback(async () => {
    const [created, rejected] = await Promise.allSettled([
      apiClient.get(ALERTS_URL),
      apiClient.get(REJECTED_ALERTS_URL),
    ]);
    if (created.status === "fulfilled") {
      addAlerts(created.value?.data || []);
    } else {
      console.error("Unable to load new ticket alerts", created.reason);
    }
    if (rejected.status === "fulfilled") {
      addAlerts((rejected.value?.data || []).map(toRejectedAlert));
    } else {
      console.error("Unable to load rejected alerts", rejected.reason);
    }
  }, [addAlerts]);

  useEffect(() => {
    const loadLoop = (src) => {
      const audio = new Audio(src);
      audio.loop = true;
      audio.preload = "auto";
      return audio;
    };
    const audios = {
      normal: loadLoop(alertSound),
      siren: loadLoop(emergencySound),
    };
    audiosRef.current = audios;

    fetchAlerts();

    // A new or rejected ticket changes the task sheet list: refresh it if
    // open, so its status and buttons don't wait for a reload
    const refreshTaskSheets = () =>
      queryClient.invalidateQueries(TASK_SHEETS_QUERY_KEY);

    const handlers = {
      [NEW_ALERT_EVENT]: (alert) => {
        addAlerts([alert]);
        refreshTaskSheets();
      },
      [ACK_EVENT]: ({ issueIds = [] } = {}) => removeAlerts(issueIds),
      [REJECTED_ALERT_EVENT]: (alert) => {
        addAlerts([toRejectedAlert(alert)]);
        refreshTaskSheets();
      },
      [REJECTED_ACK_EVENT]: ({ approvalIds = [] } = {}) =>
        removeAlerts(approvalIds.map(rejectedKey)),
    };
    const socket = acquireSocket();
    Object.entries(handlers).forEach(([event, handler]) =>
      socket.on(event, handler)
    );
    // Pick up anything raised while the connection was down
    socket.io.on("reconnect", fetchAlerts);

    return () => {
      // The connection is shared: remove only this component's listeners
      Object.entries(handlers).forEach(([event, handler]) =>
        socket.off(event, handler)
      );
      socket.io.off("reconnect", fetchAlerts);
      releaseSocket();
      clearTimeout(stopTimerRef.current);
      removeGestureHandler();
      audios.normal.pause();
      audios.siren.pause();
      audiosRef.current = null;
    };
  }, [addAlerts, fetchAlerts, removeAlerts, queryClient]);

  const closeAlerts = async (ids) => {
    const closed = alerts.filter((alert) => ids.includes(alert.id));
    removeAlerts(ids);
    const issueIds = closed
      .filter((alert) => alert.kind !== "rejected")
      .map((alert) => alert.id);
    const approvalIds = closed
      .filter((alert) => alert.kind === "rejected")
      .map((alert) => alert.approvalId);
    try {
      await Promise.all([
        issueIds.length && apiClient.post(`${ALERTS_URL}/ack`, { issueIds }),
        approvalIds.length &&
          apiClient.post(`${REJECTED_ALERTS_URL}/ack`, { approvalIds }),
      ]);
    } catch (error) {
      handleToastMsg("Unable to close the alert. Please try again.", "error");
      addAlerts(closed);
    }
  };

  // Closes the popup and opens the ticket's resolve form
  const resolveTicket = (alert) => {
    closeAlerts([alert.id]);
    navigate(
      `${RESOLVE_PATH}?slNo=${encodeURIComponent(alert.slNo)}&action=resolve`
    );
  };

  if (!alerts.length) return null;

  // Newest alert on top; older ones peek out underneath
  const stack = alerts.slice(-(MAX_STACKED + 1));
  const hiddenCount = alerts.length - stack.length;

  return (
    <div className={styles.backdrop} role="region" aria-label="New ticket alerts">
      <div className={styles.stack}>
        {stack.map((alert, index) => {
          const depth = stack.length - 1 - index;
          const isTop = depth === 0;
          const offset = depth * STACK_OFFSET_PX;
          const isRejected = alert.kind === "rejected";

          return (
            <div
              key={alert.id}
              className={styles.popup}
              style={{
                zIndex: 1000 + index,
                transform: `translate(calc(-50% + ${offset}px), calc(-50% + ${offset}px))`,
              }}
              role={isTop ? "alertdialog" : undefined}
              aria-hidden={!isTop}
              aria-labelledby={isTop ? `ticket-alert-${alert.id}` : undefined}
            >
              <button
                type="button"
                className={styles.closeButton}
                aria-label="Dismiss alert"
                onClick={() => closeAlerts([alert.id])}
                tabIndex={isTop ? 0 : -1}
              >
                <CloseIcon fontSize="small" />
              </button>

              <div className={styles.iconWrap}>
                <WarningRoundedIcon className={styles.icon} />
              </div>

              {alert.isEmergency && (
                <span className={styles.emergency}>Emergency</span>
              )}
              <h2 id={`ticket-alert-${alert.id}`} className={styles.title}>
                {!isRejected
                  ? "New Ticket Raised"
                  : alert.rejectedCount > 1
                  ? `Solution Rejected (Attempt ${alert.rejectedCount})`
                  : "Solution Rejected"}
              </h2>
              <p className={styles.subtitle}>
                Sl No. {alert.slNo} · Hall {alert.hall}
              </p>

              {isRejected && (
                <div className={styles.comment}>
                  <span className={styles.commentLabel}>
                    Comment{alert.rejectedBy ? ` by ${alert.rejectedBy}` : ""}
                  </span>
                  <p className={styles.commentText}>{alert.remarks}</p>
                </div>
              )}

              <dl className={styles.details}>
                {isRejected && (
                  <>
                    <dt>Your Solution</dt>
                    <dd className={styles.issue}>{alert.solution || "-"}</dd>

                    <dt>Rejected On</dt>
                    <dd>
                      {dayjs(alert.rejectedAt).format("DD MMM YYYY, hh:mm A")}
                    </dd>
                  </>
                )}

                <dt>Reported By</dt>
                <dd>{alert.reportedBy || "-"}</dd>

                {alert.employeeName && (
                  <>
                    <dt>Employee</dt>
                    <dd>{alert.employeeName}</dd>
                  </>
                )}

                {alert.machineName && (
                  <>
                    <dt>Machine</dt>
                    <dd>{alert.machineName}</dd>
                  </>
                )}

                {alert.reason && (
                  <>
                    <dt>Reason</dt>
                    <dd>{alert.reason}</dd>
                  </>
                )}

                <dt>Department</dt>
                <dd>{alert.department?.join(", ") || "-"}</dd>

                <dt>Issue</dt>
                <dd className={styles.issue}>{alert.description}</dd>

                <dt>Reported On</dt>
                <dd>
                  {dayjs(alert.createdAt || alert.date).format(
                    "DD MMM YYYY, hh:mm A"
                  )}
                </dd>

                <dt>Status</dt>
                <dd className={styles.status}>
                  {isRejected ? "Rejected" : "Pending"}
                </dd>
              </dl>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.viewButton}
                  onClick={() => resolveTicket(alert)}
                  tabIndex={isTop ? 0 : -1}
                >
                  Resolve
                </button>
              </div>

              {isTop && alerts.length > 1 && (
                <div className={styles.footer}>
                  <span className={styles.count}>
                    {alerts.length} open alerts
                    {hiddenCount > 0 && ` (${hiddenCount} more behind)`}
                  </span>
                  <button
                    type="button"
                    className={styles.closeAllButton}
                    onClick={() => closeAlerts(alerts.map((item) => item.id))}
                  >
                    Close all
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default NewTicketAlert;
